import { apiFetch } from './api';

// ── Commercial domain (Product → Tariff → Launch) ───────────────────────────
export interface TariffNode {
  id: string;
  productId: string | null;
  sku: string;
  name: string;
  kind: string;
  billingType: string | null;
  durationDays: number | null;
  priceCents: number;
  currency: string;
  active: boolean;
  matchers: unknown[];
  payments30d: number;
  revenue30dCents: number;
}
export interface LaunchNode {
  id: string;
  productId: string | null;
  tariffId: string | null;
  slug: string;
  name: string;
  startsAt: string | null;
  endsAt: string | null;
  sourceType: string | null;
  active: boolean;
  matchers: unknown[];
  payments30d: number;
  revenue30dCents: number;
}
export interface ProductNode {
  id: string;
  slug: string;
  name: string;
  kind: string;
  active: boolean;
  payments30d: number;
  revenue30dCents: number;
  tariffs: TariffNode[];
  launches: LaunchNode[];
}
export interface ProductTree {
  products: ProductNode[];
  ungroupedTariffs: TariffNode[];
}

export interface UnrecognizedTariff {
  rawTariff: string;
  count: number;
}

export interface AdminAuthUser {
  id: string;
  email: string;
  role: 'ADMIN' | 'ANALYST' | 'READER';
  status: 'active' | 'disabled';
  lastLoginAt: string | null;
  createdAt: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: 'ADMIN' | 'ANALYST' | 'READER';
  invitedByEmail: string;
  expiresAt: string;
}

export interface AuditRow {
  id: string;
  actorId: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  ip: string | null;
  userAgent: string | null;
  meta: Record<string, unknown> | null;
  occurredAt: string;
}

export function fetchProductTree(): Promise<ProductTree> {
  return apiFetch('/admin/products');
}

export async function fetchUnrecognizedTariffs(): Promise<UnrecognizedTariff[]> {
  const r = await apiFetch<{ items: UnrecognizedTariff[] }>('/admin/products/unrecognized-tariffs');
  return r.items;
}

// Product (parent grouping)
export function createProductGroup(body: { name: string; kind: string; slug?: string; active?: boolean }): Promise<{ id: string; slug: string }> {
  return apiFetch('/admin/products/products', { method: 'POST', body });
}
export function updateProductGroup(id: string, body: Partial<{ name: string; kind: string; active: boolean }>): Promise<{ ok: true }> {
  return apiFetch(`/admin/products/products/${id}`, { method: 'PATCH', body });
}
export function deleteProductGroup(id: string): Promise<{ ok: true }> {
  return apiFetch(`/admin/products/products/${id}`, { method: 'DELETE' });
}

// Tariff
export function createTariff(body: {
  productId?: string | null; sku: string; name: string; kind: string;
  billingType?: string | null; durationDays?: number | null; priceCents: number;
  currency?: string; active?: boolean; matchers: unknown[];
}): Promise<{ id: string; sku: string; backfixedPayments: number }> {
  return apiFetch('/admin/products/tariffs', { method: 'POST', body });
}
export function updateTariff(id: string, body: Partial<{
  productId: string | null; name: string; kind: string; billingType: string | null;
  durationDays: number | null; priceCents: number; currency: string; active: boolean; matchers: unknown[];
}>): Promise<{ ok: true; backfixedPayments: number }> {
  return apiFetch(`/admin/products/tariffs/${id}`, { method: 'PATCH', body });
}

// Launch
export function createLaunch(body: {
  productId?: string | null; tariffId?: string | null; name: string; slug?: string;
  startsAt?: string | null; endsAt?: string | null; sourceType?: string | null;
  active?: boolean; matchers?: unknown[];
}): Promise<{ id: string; slug: string; backfixedPayments: number }> {
  return apiFetch('/admin/products/launches', { method: 'POST', body });
}
export function updateLaunch(id: string, body: Partial<{
  productId: string | null; tariffId: string | null; name: string;
  startsAt: string | null; endsAt: string | null; sourceType: string | null; active: boolean; matchers: unknown[];
}>): Promise<{ ok: true; backfixedPayments: number }> {
  return apiFetch(`/admin/products/launches/${id}`, { method: 'PATCH', body });
}
export function deleteLaunch(id: string): Promise<{ ok: true }> {
  return apiFetch(`/admin/products/launches/${id}`, { method: 'DELETE' });
}

export async function fetchAdminUsers(): Promise<{
  users: AdminAuthUser[];
  pendingInvitations: PendingInvitation[];
}> {
  return apiFetch('/admin/users');
}

export interface CreatedInvitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  /** Готовий URL для accept — admin копіює і передає юзеру. Показується ОДИН раз. */
  acceptUrl: string;
}

export function createInvitation(body: { email: string; role: string }): Promise<CreatedInvitation> {
  return apiFetch('/auth/invitations', { method: 'POST', body });
}

/** Скасувати pending invitation. Старий URL стає недійсним. */
export function revokeInvitation(id: string): Promise<{ ok: true }> {
  return apiFetch(`/auth/invitations/${id}`, { method: 'DELETE' });
}

export interface BulkInviteResult {
  email: string;
  role: string;
  status: 'ok' | 'error';
  acceptUrl?: string;
  id?: string;
  code?: string;
  message?: string;
}

export function bulkInvite(
  invites: { email: string; role: string }[],
): Promise<{ results: BulkInviteResult[]; succeeded: number; failed: number }> {
  return apiFetch('/auth/invitations/bulk', { method: 'POST', body: { invites } });
}

export interface PasswordResetCreated {
  id: string;
  email: string;
  expiresAt: string;
  /** Готовий URL для скидання пароля. Показується ОДИН раз. */
  resetUrl: string;
}

/** Admin генерує одноразовий reset-link для існуючого юзера. */
export function generatePasswordReset(userId: string): Promise<PasswordResetCreated> {
  return apiFetch(`/admin/users/${userId}/password-reset`, { method: 'POST' });
}

/** Public: користувач ставить новий пароль через reset-token. */
export function acceptPasswordReset(body: {
  token: string;
  password: string;
}): Promise<{ userId: string; email: string }> {
  return apiFetch('/auth/password-reset/accept', { method: 'POST', body });
}

export function updateUserRole(id: string, role: string): Promise<unknown> {
  return apiFetch(`/admin/users/${id}/role`, { method: 'PATCH', body: { role } });
}

export function updateUserStatus(id: string, status: 'active' | 'disabled'): Promise<unknown> {
  return apiFetch(`/admin/users/${id}/status`, { method: 'PATCH', body: { status } });
}

export async function fetchAuditLog(rangeDays = 7): Promise<AuditRow[]> {
  const r = await apiFetch<{ items: AuditRow[] }>(`/audit?rangeDays=${rangeDays}`);
  return r.items;
}

export interface WebhookInfoEntry {
  url: string;
  method: string;
  contentType: string;
  secretConfigured?: boolean;
  /** Звідки взявся секрет: 'db' (через UI) / 'env' (bootstrap) / null. */
  secretSource?: 'db' | 'env' | null;
  responseFormat?: string;
  eventType?: string;
  instructions: string;
}

export interface WebhookInfo {
  wayforpay: WebhookInfoEntry;
  sendpulse: WebhookInfoEntry;
}

export interface WebhookLogRow {
  id: string;
  action: string; // 'webhook.<source>.<event>'
  targetType: string | null;
  targetId: string | null;
  occurredAt: string;
  meta: Record<string, unknown> | null;
}

export interface WebhookLogSummary {
  action: string;
  count: number;
}

// ---------------------------------------------------------------------------
// Bulk actions
// ---------------------------------------------------------------------------

export function bulkDeletePersons(ids: string[]): Promise<{ deleted: number }> {
  return apiFetch('/admin/persons/bulk-delete', { method: 'POST', body: { ids } });
}

export function createPerson(body: {
  displayName?: string;
  username?: string;
  email?: string;
  phone?: string;
  telegramId?: string;
}): Promise<{ id: string }> {
  return apiFetch('/admin/persons', { method: 'POST', body });
}

export function updatePerson(
  id: string,
  body: {
    displayName?: string | null;
    username?: string | null;
    currentTariff?: string | null;
    currentSubscriptionStatus?: string | null;
    subscriptionEndsAt?: string | null;
    /** Частковий merge у custom_fields jsonb. Передати null/'' = видалити ключ. */
    customFields?: Record<string, unknown>;
  },
): Promise<{ ok: true }> {
  return apiFetch(`/admin/persons/${id}`, { method: 'PATCH', body });
}

export function bulkImportPersons(
  rows: Record<string, unknown>[],
): Promise<{ imported: number; total: number; skipped: number; duplicatesSkipped: number }> {
  return apiFetch('/admin/persons/bulk-import', { method: 'POST', body: { rows } });
}

// =============================================================================
//  Import runs history (Settings → Імпорт даних → Історія)
// =============================================================================

export interface ImportRunListItem {
  id: string;
  tableSlug: string;
  sourceFilename: string | null;
  totalRows: number;
  createdCount: number;
  enrichedCount: number;
  mergedCount: number;
  invalidCount: number;
  emptyCount: number;
  durationMs: number | null;
  startedAt: string;
  finishedAt: string | null;
  actorId: string | null;
  actorEmail: string | null;
}

export interface ImportRunRow {
  id: string;
  rowNumber: number;
  action: 'created' | 'enriched' | 'merged' | 'skipped' | 'failed' | string;
  personId: string | null;
  mergedLoserIds: string[];
  matchedBy: string[];
  identifiers: Record<string, unknown>;
  message: string | null;
  error: string | null;
}

export function fetchImportRuns(): Promise<{ items: ImportRunListItem[] }> {
  return apiFetch('/admin/import-runs');
}

export function fetchImportRun(
  id: string,
): Promise<{ run: ImportRunListItem; rows: ImportRunRow[] }> {
  return apiFetch(`/admin/import-runs/${id}`);
}

export function fetchImportRunsByPerson(personId: string): Promise<{
  items: (ImportRunRow & {
    run: { id: string; tableSlug: string; sourceFilename: string | null; startedAt: string };
  })[];
}> {
  return apiFetch(`/admin/import-runs/by-person/${personId}`);
}

export function createPayment(body: {
  amountUah: number;
  currency?: string;
  status: 'SUCCEEDED' | 'REFUNDED' | 'FAILED' | 'PENDING';
  occurredAt: string;
  externalRef?: string;
  provider?: 'WAYFORPAY' | 'LIQPAY' | 'FONDY' | 'PORTMONE' | 'STRIPE' | 'UNKNOWN';
  payerEmail?: string;
  payerPhone?: string;
  payerFirstName?: string;
  payerLastName?: string;
  rawTariff?: string;
  paymentMethod?: string;
  cardLast4?: string;
}): Promise<{ id: string }> {
  return apiFetch('/admin/payments', { method: 'POST', body });
}

export function updatePayment(
  id: string,
  body: {
    /** Частковий merge у custom_fields jsonb. Передати null/'' = видалити ключ. */
    customFields?: Record<string, unknown>;
  },
): Promise<{ ok: true }> {
  return apiFetch(`/admin/payments/${id}`, { method: 'PATCH', body });
}

export function bulkImportPayments(
  rows: Record<string, unknown>[],
): Promise<{
  imported: number;
  total: number;
  skipped: number;
  duplicatesSkipped: number;
  invalid: number;
  errors: { row: number; reason: string }[];
}> {
  return apiFetch('/admin/payments/bulk-import', { method: 'POST', body: { rows } });
}

export function bulkDeletePayments(
  ids: string[],
): Promise<{ deleted: number; affectedPersons: number }> {
  return apiFetch('/admin/payments/bulk-delete', { method: 'POST', body: { ids } });
}

/** Усі IDs під поточним фільтром (для "Вибрати все"). max 10k. */
export function fetchPersonIds(qs: string): Promise<{ ids: string[] }> {
  return apiFetch(`/admin/persons/ids?${qs}`);
}
export function fetchPaymentIds(qs: string): Promise<{ ids: string[] }> {
  return apiFetch(`/admin/payments/ids?${qs}`);
}

// ---------------------------------------------------------------------------
// Saved filter presets
// ---------------------------------------------------------------------------

export interface SavedFilter {
  id: string;
  name: string;
  scope: string;
  tree: unknown;
  /** Snapshot сортування у момент збереження ("вид" = filter + sort). */
  sort?: string | null;
  updatedAt: string;
}

export async function fetchSavedFilters(scope: string): Promise<SavedFilter[]> {
  const r = await apiFetch<{ items: SavedFilter[] }>(`/saved-filters?scope=${encodeURIComponent(scope)}`);
  return r.items;
}
export function createSavedFilter(
  scope: string,
  name: string,
  tree: unknown,
  sort?: string | null,
): Promise<SavedFilter> {
  return apiFetch('/saved-filters', { method: 'POST', body: { scope, name, tree, sort } });
}
export function deleteSavedFilter(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/saved-filters/${id}`, { method: 'DELETE' });
}

export function fetchWebhookInfo(): Promise<WebhookInfo> {
  return apiFetch('/admin/webhooks/info');
}

export function setWayforpaySecret(secret: string): Promise<{ ok: boolean }> {
  return apiFetch('/admin/webhooks/wayforpay/secret', {
    method: 'PUT',
    body: { secret },
  });
}

export function clearWayforpaySecret(): Promise<{ ok: boolean }> {
  return apiFetch('/admin/webhooks/wayforpay/secret', { method: 'DELETE' });
}

export interface WebhookLogDetail {
  log: {
    id: string;
    action: string;
    targetType: string | null;
    targetId: string | null;
    actorId: string | null;
    ip: string | null;
    userAgent: string | null;
    occurredAt: string;
    meta: Record<string, unknown> | null;
  } | null;
  eventRaw: {
    id: string;
    type: string;
    receivedAt: string;
    occurredAt: string;
    processed: boolean;
    processedAt: string | null;
    processingError: string | null;
    payload: unknown;
  }[];
  payment: {
    id: string;
    occurredAt: string;
    amountCents: number;
    currency: string;
    status: string;
    rawStatus: string | null;
    externalRef: string | null;
    paymentMethod: string | null;
    cardBrand: string | null;
    cardLast4: string | null;
    payerEmail: string | null;
    payerPhone: string | null;
    payerFirstName: string | null;
    payerLastName: string | null;
    feeCents: number | null;
    netCents: number | null;
    rawTariff: string | null;
    product: { sku: string; name: string } | null;
    person: {
      id: string;
      displayName: string | null;
      primaryEmail: string | null;
      primaryPhone: string | null;
      username: string | null;
    } | null;
    meta: Record<string, unknown> | null;
    rawData: unknown;
  } | null;
}

export function fetchWebhookLogDetail(id: string): Promise<WebhookLogDetail> {
  return apiFetch(`/admin/webhooks/logs/${id}`);
}

export function fetchWebhookLogs(opts: {
  source?: 'wayforpay-webhook' | 'sendpulse-payment-order';
  limit?: number;
  cursor?: string;
}): Promise<{
  items: WebhookLogRow[];
  nextCursor: string | null;
  summary: WebhookLogSummary[];
}> {
  const p = new URLSearchParams();
  if (opts.source) p.set('source', opts.source);
  if (opts.limit) p.set('limit', String(opts.limit));
  if (opts.cursor) p.set('cursor', opts.cursor);
  return apiFetch(`/admin/webhooks/logs?${p.toString()}`);
}
